import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Users, Target, Award, Heart, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import NewsletterSubscribe from "../components/NewsletterSubscribe";
import { useTranslation } from "react-i18next";

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white/5 backdrop-blur p-6 shadow ${className}`}
  >
    {children}
  </div>
);

const Image = ({ src, alt, className = "", ...props }) => (
  <img src={src} alt={alt} className={className} {...props} />
);
const useCountUpOnVisible = (targetNumber, duration = 2000) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    let observer;
    let started = false;

    const startAnimation = () => {
      if (started) return;
      started = true;

      const startTime = performance.now();

      const animate = (time) => {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const currentValue = Math.floor(progress * targetNumber);
        setValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    };

    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) startAnimation();
      },
      { threshold: 0.3 },
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [targetNumber, duration]);

  return { ref, value };
};

const useOnScreen = (options) => {
  const ref = useRef(null);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        if (ref.current) obs.unobserve(ref.current);
      }
    }, options);

    if (ref.current) obs.observe(ref.current);

    return () => ref.current && obs.unobserve(ref.current);
  }, [ref, options]);

  return [ref, isVisible];
};

const AnimatedElement = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useOnScreen({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ================================
   PAGE HOME
   ================================ */
const StatItem = ({ icon: Icon, number, label, delay = 0 }) => {
  const { ref, value } = useCountUpOnVisible(
    parseInt(number),
    1800 + delay * 200,
  );

  return (
    <AnimatedElement delay={delay * 150}>
      <div className="flex items-start gap-4">
        <Icon className="h-10 w-10 text-electric-blue drop-shadow-[0_0_10px_#7DF9FF90]" />

        <div>
          <span
            ref={ref}
            className="text-5xl font-extrabold font-counter block text-white"
          >
            {value}+
          </span>
          <span className="opacity-80 text-lg">{label}</span>
        </div>
      </div>
    </AnimatedElement>
  );
};

export default function HomePage() {
  const { t } = useTranslation();

  const [activeActivity, setActiveActivity] = useState(0);

  const testimonials = [
    {
      name: t("home.testimonials.member1Name"),
      role: t("home.testimonials.member1Role"),
      content: t("home.testimonials.member1Content"),
      image: "/images/membre2.webp",
      alt: "Membre - 17ans",
    },
    {
      name: t("home.testimonials.member2Name"),
      role: t("home.testimonials.member2Role"),
      content: t("home.testimonials.member2Content"),
      image: "/images/membre1.jpeg",
      alt: "Membre - 18ans",
    },
    {
      name: t("home.testimonials.member3Name"),
      role: t("home.testimonials.member3Role"),
      content: t("home.testimonials.member3Content"),
      image: "/images/membre3.jpeg",
      alt: "Membre - 17ans",
    },
  ];

  const activities = [
    {
      title: t("home.activities.items.leadership"),
      description: t("home.activities.descriptions.leadership"),
      image: "/images/pr1.jpg",
      alt: "Formation au leadership",
    },
    {
      title: t("home.activities.items.community"),
      description: t("home.activities.descriptions.community"),
      image: "/images/pr2.webp",
      alt: "Actions communautaires",
    },
    {
      title: t("home.activities.items.innovation"),
      description: t("home.activities.descriptions.innovation"),
      image: "/images/pr3.jpg",
      alt: "Laboratoire d'innovation",
    },
  ];

  const stats = [
    { icon: Users, number: "50+", label: "Membres actifs" },
    { icon: Target, number: "3+", label: "Projets réalisés" },
    { icon: Award, number: "25+", label: "Récompenses" },
    { icon: Heart, number: "10000+", label: "Vies impactées" },
  ];

  return (
    <div className="bg-background text-foreground font-paragraph overflow-clip">
      <section className="relative w-full h-screen overflow-hidden flex items-center justify-start bg-black">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero.webp"
            alt="Background"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent z-[1]"></div>

        <div className="relative z-[2] w-full max-w-[120rem] mx-auto px-6 md:px-12">
          <div className="max-w-[900px]">
            <h1
              className="
          animate-title
          font-heading 
          text-white 
          uppercase 
          font-bold
          leading-[1.05]
          tracking-[0.14em]
          text-4xl sm:text-5xl md:text-6xl lg:text-[5.5rem]
        "
            >
              <span className="block">LEAD WITH</span>

              <span className="relative inline-block mt-4">
                EXCELLENCE
                <span className="absolute left-0 bottom-[12%] w-full h-[25%] bg-[#7DF9FF] -z-10 -skew-x-12"></span>
              </span>
            </h1>

            <p className="animate-subtitle font-paragraph text-lg md:text-xl text-white/80 mt-6 max-w-2xl leading-relaxed">
              {t("home.hero.description")}
            </p>

            <div className="animate-buttons mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="/members"
                className="bg-[#007BFF] hover:bg-[#0066d6] text-white px-8 py-2 rounded-md font-semibold text-base shadow-md transition"
              >
                {t("home.hero.join")} →
              </a>

              <a
                href="/about"
                className="border border-white text-white hover:bg-white hover:text-black px-8 py-2 rounded-md font-semibold text-base transition"
              >
                {t("home.hero.learnMore")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===========================
          SECTION ABOUT PREVIEW
      ============================ */}
      <section className="bg-background mt-40 mb-20">
        <div className="max-w-[120rem] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-20">
          <AnimatedElement className="self-center">
            <h2 className="font-heading text-5xl md:text-6xl font-bold text-foreground mb-10">
              {t("home.aboutPreview.title")}{" "}
              <span className="text-primary">
                {t("home.aboutPreview.highlight")}
              </span>
            </h2>

            <div className="space-y-8 text-lg text-foreground/80 leading-relaxed">
              <p className="opacity-80 mb-6">
                {t("home.aboutPreview.paragraph1")}
              </p>

              <p className="opacity-80 mb-10">
                {t("home.aboutPreview.paragraph2")}
              </p>
            </div>

            <Button className="bg-secondary text-white mt-10">
              <Link to="/about">{t("home.aboutPreview.button")}</Link>
            </Button>
          </AnimatedElement>

          <AnimatedElement
            delay={100}
            className="relative h-[500px] lg:h-[600px]"
          >
            <Image
              src="/images/p1.webp"
              alt="Jeune leader"
              className="object-cover rounded-lg w-full h-full"
            />
          </AnimatedElement>
        </div>
      </section>

      {/* ===========================
          SECTION ACTIVITÉS
      ============================ */}
      <section className="py-32 bg-blue-50">
        <div className="max-w-[120rem] mx-auto px-4 sm:px-6 md:px-12">
          <AnimatedElement className="text-center max-w-3xl mx-auto">
            <h2
              className="
        font-heading 
        text-4xl 
        sm:text-5xl 
        md:text-6xl 
        font-bold 
        text-foreground 
        mb-6
      "
            >
              {t("home.activities.before")}{" "}
              <span className="text-primary">
                {" "}
                {t("home.activities.highlight")}
              </span>{" "}
              {t("home.activities.after")}
            </h2>

            <p className="font-paragraph text-base sm:text-lg text-foreground/80">
              {t("home.activities.description")}
            </p>
          </AnimatedElement>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-4 gap-8">
            <AnimatedElement className="lg:col-span-1 flex flex-col gap-3">
              {activities.map((activity, index) => (
                <button
                  key={index}
                  onClick={() => setActiveActivity(index)}
                  className={`
              w-full 
              px-4 py-3 
              text-left 
              font-heading font-bold 
              text-lg 
              rounded-md 
              transition 
              ${
                activeActivity === index
                  ? "bg-background text-primary shadow-md"
                  : "bg-transparent text-foreground/60 hover:bg-background/50"
              }
            `}
                >
                  <span className="text-primary mr-2">0{index + 1}</span>
                  {activity.title}
                </button>
              ))}
            </AnimatedElement>

            <div className="lg:col-span-3 relative min-h-[430px]">
              {activities.map((activity, index) => (
                <div
                  key={index}
                  className={`
              absolute inset-0 
              grid grid-cols-1 md:grid-cols-2 
              gap-6 md:gap-8 
              items-center 
              transition-opacity duration-500
              ${activeActivity === index ? "opacity-100" : "opacity-0 pointer-events-none"}
            `}
                >
                  <div className="w-full h-[240px] sm:h-[320px] md:h-full">
                    <img
                      src={activity.image}
                      alt={activity.title}
                      className="w-full h-full object-cover rounded-md shadow-md"
                    />
                  </div>

                  <div className="p-2 sm:p-4">
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                      {activity.title}
                    </h3>
                    <p className="font-paragraph text-foreground/80 mb-8">
                      {activity.description}
                    </p>

                    <Button className="border border-primary text-primary px-6 py-2 rounded-md">
                      <Link to="/activities">
                        {t("home.activities.button")}
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===========================
          SECTION GALLERIE
      ============================ */}
      <section className="py-32 bg-foreground text-background">
        <div className="max-w-[120rem] mx-auto px-6 md:px-12">
          <AnimatedElement className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              {t("home.gallery.title1")}{" "}
              <span className="text-electric-blue">
                {t("home.gallery.title2")}
              </span>
            </h2>
            <p className="opacity-80 font-paragraph text-lg ">
              {t("home.gallery.description")}
            </p>
          </AnimatedElement>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Image
                src="/images/p2.webp"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div>
              <Image
                src="/images/p31.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div>
              <Image
                src="/images/p12.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div>
              <Image
                src="/images/p5.jpg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div className="lg:col-span-2">
              <Image
                src="/images/p17.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div>
              <Image
                src="/images/p6.webp"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div>
              <Image
                src="/images/p7.jpg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full mb-4 object-cover rounded-xl aspect-square"
              />
              <Image
                src="/images/p9.jpg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full  object-cover rounded-xl aspect-square"
              />
            </div>
            <div>
              <Image
                src="/images/p10.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>

            <div>
              <Image
                src="/images/p36.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>
            <div>
              <Image
                src="/images/p13.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>
            <div>
              <Image
                src="/images/p14.jpeg"
                alt="Activité de l'association ALEOA"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-xl aspect-square"
              />
            </div>
          </div>
          <div className="flex justify-center mt-12">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-electric-blue text-white font-semibold hover:scale-105 transition-transform"
            >
              {t("home.gallery.button")}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===========================
          SECTION TESTIMONIALS
      ============================ */}
      <section className="w-full bg-background py-16 md:py-32">
        <div className="w-full max-w-[120rem] mx-auto px-4 sm:px-6 md:px-12">
          <AnimatedElement className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-4 md:mb-6">
              {t("home.testimonials.title1")}{" "}
              <span className="text-electric-blue">
                {t("home.testimonials.title2")}
              </span>
            </h2>
            <p className="font-paragraph text-base md:text-lg text-foreground/80">
              {t("home.testimonials.description")}
            </p>
          </AnimatedElement>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t, index) => (
              <AnimatedElement key={index} delay={index * 100}>
                <Card
                  className="
            h-full bg-transparent border-foreground/10 
            p-6 md:p-8 flex flex-col justify-between 
            min-h-[260px] md:min-h-[320px]
          "
                >
                  <p className="font-paragraph text-base md:text-lg italic opacity-80">
                    "{t.content}"
                  </p>

                  <div className="flex items-center mt-6 md:mt-8 pt-4 md:pt-6 border-t border-foreground/10">
                    <Image
                      src={t.image}
                      className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h4 className="font-heading font-bold text-base md:text-lg text-foreground">
                        {t.name}
                      </h4>
                      <p className="font-paragraph text-sm text-primary">
                        {t.role}
                      </p>
                    </div>
                  </div>
                </Card>
              </AnimatedElement>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-foreground text-background py-20 md:py-32">
        <div className="w-full max-w-[120rem] mx-auto px-6 md:px-12">
          <div className="bg-primary p-10 md:p-16 grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedElement>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
                {t("home.cta.title")}
              </h2>
              <p className="font-paragraph text-lg text-primary-foreground/80 mb-8 max-w-lg">
                {t("home.cta.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/members"
                  className="inline-flex items-center justify-center px-8 h-10 rounded-md bg-background text-foreground hover:bg-background/20"
                >
                  {t("home.cta.becomeMember")}
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 h-10 rounded-lg border border-background/50 text-primary-foreground font-semibold hover:bg-primary-foreground hover:text-primary"
                >
                  {t("home.cta.contact")}
                </Link>
              </div>
            </AnimatedElement>

            <AnimatedElement delay={120}>
              <h3 className="font-heading text-2xl font-bold text-primary-foreground mb-4 mt-10">
                {t("home.cta.newsletterTitle")}
              </h3>
              <p className="font-paragraph text-primary-foreground/80 mb-6">
                {t("home.cta.newsletterDescription")}
              </p>
              <NewsletterSubscribe />
            </AnimatedElement>
          </div>
        </div>
      </section>
    </div>
  );
}
